import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';

export interface ListState {
  page: number;
  search: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  setSort: (column: string) => void;
  getFilter: (key: string) => string | null;
  setFilter: (key: string, value: string | null) => void;
}

/**
 * Manages list state (page, search, sort, filters) either in URL query
 * params or in local component state, based on the `urlParams` flag.
 *
 * Both hooks are always called to satisfy the Rules of Hooks.
 * URL mode uses { replace: true } to avoid polluting browser history.
 * Search, sort, and filter changes always reset page to 1.
 */
export function useListState(urlParams: boolean): ListState {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localPage, setLocalPageState] = useState(1);
  const [localSearch, setLocalSearchState] = useState('');
  const [localSort, setLocalSortState] = useState<{ by: string; dir: 'asc' | 'desc' }>({
    by: '',
    dir: 'asc',
  });
  const [localFilters, setLocalFilters] = useState<Record<string, string | null>>({});

  // ── URL-based values ──────────────────────────────────────────────────────
  const urlPage = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));
  const urlSearch = searchParams.get('search') ?? '';
  const urlSortBy = searchParams.get('sort') ?? '';
  const urlSortDir: 'asc' | 'desc' = searchParams.get('dir') === 'desc' ? 'desc' : 'asc';

  const setUrlPage = useCallback(
    (p: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (p <= 1) next.delete('page');
          else next.set('page', String(p));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setUrlSearch = useCallback(
    (s: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!s) next.delete('search');
          else next.set('search', s);
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setUrlSort = useCallback(
    (column: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const currentColumn = prev.get('sort') ?? '';
          const currentDir = prev.get('dir') === 'desc' ? 'desc' : 'asc';
          const nextDir = currentColumn === column && currentDir === 'asc' ? 'desc' : 'asc';
          next.set('sort', column);
          if (nextDir === 'desc') next.set('dir', 'desc');
          else next.delete('dir');
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const getUrlFilter = useCallback((key: string) => searchParams.get(key), [searchParams]);

  const setUrlFilter = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!value) next.delete(key);
          else next.set(key, value);
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // ── Local-state-based setters ─────────────────────────────────────────────
  const setLocalPage = useCallback((p: number) => setLocalPageState(p), []);

  const setLocalSearch = useCallback((s: string) => {
    setLocalSearchState(s);
    setLocalPageState(1);
  }, []);

  const setLocalSort = useCallback((column: string) => {
    setLocalSortState((prev) => ({
      by: column,
      dir: prev.by === column && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
    setLocalPageState(1);
  }, []);

  const getLocalFilter = useCallback((key: string) => localFilters[key] ?? null, [localFilters]);

  const setLocalFilter = useCallback((key: string, value: string | null) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
    setLocalPageState(1);
  }, []);

  // ── Pick based on mode ────────────────────────────────────────────────────
  if (urlParams) {
    return {
      page: urlPage,
      search: urlSearch,
      sortBy: urlSortBy,
      sortDir: urlSortDir,
      setPage: setUrlPage,
      setSearch: setUrlSearch,
      setSort: setUrlSort,
      getFilter: getUrlFilter,
      setFilter: setUrlFilter,
    };
  }

  return {
    page: localPage,
    search: localSearch,
    sortBy: localSort.by,
    sortDir: localSort.dir,
    setPage: setLocalPage,
    setSearch: setLocalSearch,
    setSort: setLocalSort,
    getFilter: getLocalFilter,
    setFilter: setLocalFilter,
  };
}
